/**
 * One brief (REDESIGN.md §7).
 *
 * The transmission block first — dark, 16:9, with a mono corner label — then
 * the eyebrow, the title, the body, and the mission as a tinted card with one
 * button. Nothing else: the way back is the header's pill, and You is one tap
 * away on the bar, so neither needs repeating here.
 *
 * The body arrives as HTML already rendered and escaped by the server
 * (lib/Markdown.php), and the video as a ready embed URL — the client never
 * builds either from raw input.
 */

import { apiGet, apiPost, ApiError } from '../js/api.js'
import { icon } from '../js/icons.js'
import { text } from '../js/html.js'
import { relativeDay } from '../js/dates.js'
import { sequenceName } from '../content/kinds.js'

export async function renderBrief(root, { id, retreat, onBack }) {
  root.innerHTML = `<p class="lede">Opening…</p>`

  let brief

  try {
    brief = (await apiGet(`/briefs/${id}`)).brief
  } catch (error) {
    root.innerHTML = `
      <p class="lede">${text(error instanceof ApiError ? error.message : 'This brief does not exist.')}</p>
      <button class="link-button" id="back">← Back</button>
    `
    root.querySelector('#back').addEventListener('click', onBack)

    return
  }

  const zone = retreat?.timezone ?? 'Europe/Zagreb'

  function draw() {
    root.innerHTML = `
      ${transmission(brief)}

      <p class="eyebrow">
        ${text(sequenceName(brief))} · ${text(relativeDay(brief.publish_at, zone))}
      </p>
      <h1 class="h1">${text(brief.title)}</h1>

      ${brief.quote ? `<blockquote class="pullquote">“${text(brief.quote)}”</blockquote>` : ''}

      <div class="prose">${brief.body_html}</div>

      ${brief.has_mission ? mission(brief) : ''}
    `

    root.querySelector('#mission-toggle')?.addEventListener('click', toggleMission)
  }

  async function toggleMission() {
    const button = root.querySelector('#mission-toggle')
    button.disabled = true

    try {
      const result = await apiPost(`/briefs/${brief.id}/mission`, { done: !brief.mission_done })
      brief.mission_done = result.mission_done
    } catch {
      // Nothing is lost if this fails; the next tap tries again.
    }

    draw()
  }

  draw()
}

/**
 * The dark block at the top. It only appears when there is something behind it
 * — an empty 16:9 rectangle is worse than no rectangle at all.
 */
function transmission(brief) {
  if (brief.video) {
    return `
      <div class="player">
        <span class="player__tc">
          ${text(sequenceName(brief).toUpperCase())}${brief.minutes ? ` · ${brief.minutes} MIN` : ''}
        </span>
        <iframe
          src="${text(brief.video.embed_url)}"
          title="${text(brief.title)}"
          loading="lazy"
          allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          referrerpolicy="no-referrer"
          allowfullscreen
        ></iframe>
      </div>
    `
  }

  if (brief.image_url) {
    return `<img class="player player--still" src="${text(brief.image_url)}" alt="" loading="lazy" />`
  }

  return ''
}

function mission(brief) {
  return `
    <aside class="mission">
      <p class="mission__tag">
        ${text(brief.number ? `MISSION ${String(brief.number).padStart(2, '0')}` : 'MISSION')}
      </p>

      <div class="mission__body">
        ${brief.mission_html || '<p>The instructions are in the brief.</p>'}
      </div>

      ${
        brief.mission_hint
          ? `<p class="mission__hint">${icon('sparkle', { size: 15 })}<span>${text(brief.mission_hint)}</span></p>`
          : ''
      }

      <button class="btn ${brief.mission_done ? 'btn--done' : ''}" id="mission-toggle">
        ${icon(brief.mission_done ? 'checkCircle' : 'check', { size: 17 })}
        <span>${brief.mission_done ? 'Marked done' : 'Mark the mission done'}</span>
      </button>
    </aside>
  `
}
